import { useState, useCallback, useEffect, ReactElement } from 'react';
import { useRoutes, useAwsEffect } from '@localstack/services';
import { Card, Button } from '@mui/material';
import { Dropdown, ButtonWithDialog, IAMPoliciesTable, PageTitle } from '@localstack/ui';
import { IAMPolicy, IAMListPoliciesResponse } from '@localstack/types';

import { DEFAULT_IAM_ROUTES } from '@localstack/constants';

import { NavTabs } from './components';
import { IAMProps } from './types';

const PAGE_SIZE = 100;

export const IAMPolicies = ({ Layout, clientOverrides, routes = DEFAULT_IAM_ROUTES }: IAMProps): ReactElement => {
  const [policies, setPolicies] = useState<IAMPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  const { goto } = useRoutes();

  const { listPolicies } = useAwsEffect('IAM', ['listPolicies'], { clientOverrides });

  let response: IAMListPoliciesResponse = { Policies: [], IsTruncated: true, Marker: undefined };

  const listAllPolicies = async () => {
    const allPolicies: IAMPolicy[] = [];

    setIsLoading(true);
    while (response.IsTruncated) {
      response = await listPolicies({ MaxItems: PAGE_SIZE, Marker: response.Marker });
      allPolicies.push(...(response.Policies ?? []));
    }

    setPolicies(allPolicies);
    setIsLoading(false);
  };

  useEffect(() => {
    listAllPolicies();
  }, []);

  const { deletePolicy } = useAwsEffect('IAM', ['deletePolicy'], {
    revalidate: ['getPolicy', 'listPolicies'],
    clientOverrides,
  });

  const handleDeleteSelected = useCallback(async () => {
    const promises = selectedIds.map((PolicyArn) => deletePolicy({ PolicyArn }));
    await Promise.all(promises);
    listAllPolicies();
  }, [selectedIds]);

  return (
    <Layout
      documentTitle="IAM: Policies"
      title={
        <PageTitle
          title="IAM Policies"
          onMutate={listAllPolicies}
          breadcrumbs={[
            ['IAM', () => goto(routes.RESOURCES_IAM)],
            ['Policies', () => goto(routes.RESOURCES_IAM_POLICIES)],
          ]}
        />
      }
      tabs={<NavTabs routes={routes} />}
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_IAM_POLICY_CREATE)}>
            Create Policy
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Delete ${selectedIds.length} Policy(es)?`}
              onClick={handleDeleteSelected}
              dialogContent="Selected Policies will be permanently deleted"
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card style={{ height: '100%' }}>
        <IAMPoliciesTable
          selectable
          loading={isLoading}
          policies={policies ?? []}
          onSelect={setSelectedIds}
          page={page}
          onPageChange={setPage}
          pageSize={PAGE_SIZE}
          onViewPolicy={({ Arn }) => goto(routes.RESOURCES_IAM_POLICY, { policyArn: encodeURIComponent(Arn || '') })}
        />
      </Card>
    </Layout>
  );
};
